import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useMemo, useState, useEffect } from "react";
import AddUserIcon from "../../Assests/Icons/AddUserIcon";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button";
import Loader from "../../Components/Loader";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useCollectionRequest } from "../../API/Queries/useCollectionRequest";

export const RecordsPerPage = 10;
export const SearchLimit = 1000;

const CreateCollectionRequest = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [allAssets, setAllAssets] = useState([]);
  const [paginatedAssets, setPaginatedAssets] = useState([]);

  const navigate = useNavigate();
  const { mutate, isLoading } = useCollectionRequest();

  const onSearch = () => {
    mutate(
      {
        page: 0,
        limit: SearchLimit,
        filters: searchValue ? [searchValue] : [],
      },
      {
        onSuccess: (res) => {
          const filtered = (res || []).filter(
            (item) => item.name?.toLowerCase() === searchValue.toLowerCase()
          );
          setAllAssets(filtered);
          setPageNumber(0);
        },
        onError: (err) => {
          console.error("Search failed", err);
          setAllAssets([]);
        },
      }
    );
  };

  const onClearSearch = () => {
    setSearchValue("");
    setPageNumber(0);
    mutate(
      {
        page: 0,
        limit: RecordsPerPage,
        filters: [],
      },
      {
        onSuccess: (res) => {
          setAllAssets(res || []);
        },
      }
    );
  };

  const onAddUser = () => navigate("/dam/create-request-collection");
  const onNext = () => setPageNumber((prev) => prev + 1);
  const onPrevious = () => setPageNumber((prev) => Math.max(0, prev - 1));

  useEffect(() => {
    if (!searchValue) {
      mutate(
        {
          page: pageNumber,
          limit: RecordsPerPage,
          filters: [],
        },
        {
          onSuccess: (res) => {
            setAllAssets(res || []);
          },
        }
      );
    }
  }, [pageNumber, searchValue]);

  useEffect(() => {
    const start = pageNumber * RecordsPerPage;
    const end = start + RecordsPerPage;
    setPaginatedAssets(allAssets.slice(start, end));
  }, [allAssets, pageNumber]);

  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: "Collection Request Id", width: 400 },
      { field: "name", headerName: "Collection Request Name", width: 300 },
      {
        field: "actions",
        headerName: "Actions",
        width: 500,
        cellRenderer: (params) =>
          Array.isArray(params.data.assets)
            ? params.data.assets
                .flatMap(
                  (asset) => asset.actions?.map((action) => action.name) || []
                )
                .join(", ")
            : "N/A",
      },
      {
        field: "view",
        headerName: "View Collection Request",
        width: 300,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <MdOutlineRemoveRedEye
              onClick={() => {
                navigate("/dam/edit-request-collection", {
                  state: { prefillData: params.data },
                });
              }}
              style={{
                color: "#47B7CF",
                fontSize: "large",
                cursor: "pointer",
              }}
            />
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(() => ({ filter: true, sortable: true }), []);

  return (
    <>
      {isLoading && <Loader />}
      <p className="flex items-center justify-center mt-16 text-lg text-[#0e76a8]">
        <b>Collection Request Template</b>
      </p>

      <div className="flex flex-wrap justify-between items-center mb-4 px-4">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search by Name"
            className="border border-gray-300 rounded px-3 py-2"
          />
          <Button onClick={onSearch}>Search</Button>
          <Button onClick={onClearSearch}>Clear</Button>
        </div>
        <Button className="flex items-center mt-2 sm:mt-0" onClick={onAddUser}>
          <AddUserIcon className="mr-1" />
          Create New Template
        </Button>
      </div>

      <div
        className="ag-theme-alpine"
        style={{ height: "calc(100vh - 260px)" }}
      >
        <AgGridReact
          rowData={paginatedAssets}
          columnDefs={columnDefs}
          animateRows
          suppressPaginationPanel
          overlayNoRowsTemplate={
            `<span style="padding: 10px; border: 1px solid #ccc; background: #f9f9f9; border-radius: 4px;">` +
            (!isLoading ? "No Data Available" : "Loading...") +
            `</span>`
          }
          suppressScrollOnNewData
          defaultColDef={defaultColDef}
        />

        <div className="mt-6 flex items-center justify-center">
          <Button disabled={pageNumber === 0 || isLoading} onClick={onPrevious}>
            Previous
          </Button>
          <p className="mx-2">Page: {pageNumber + 1}</p>
          <Button
            disabled={
              (pageNumber + 1) * RecordsPerPage >= allAssets.length || isLoading
            }
            onClick={onNext}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default CreateCollectionRequest;